import { Link } from "react-router-dom";
import React, { useState } from "react";
import {
  FiArrowUpRight,
  FiBriefcase,
  FiCheckCircle,
  FiLayers,
  FiPlus,
  FiUsers,
  FiX,
  FiBarChart2,
  FiShield,
  FiMessageSquare,
} from "react-icons/fi";

import "../styles/ProfessionalServices.css";

const capabilityCards = [
  {
    icon: <FiBriefcase />,
    title: "Positioning & credibility",
    text: "Digital foundations that make your people, expertise, services, and proof easier to understand.",
    image:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=85",
  },
  {
    icon: <FiUsers />,
    title: "Client journeys",
    text: "Thoughtful digital paths for enquiries, onboarding, collaboration, communication, and service delivery.",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=85",
  },
  {
    icon: <FiLayers />,
    title: "Practice platforms",
    text: "Useful portals, dashboards, and connected systems that bring client work and operations into view.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=85",
  },
];

const services = [
  [
    "Professional websites",
    "Confident digital presences built to explain expertise, establish trust, and convert interest.",
    <FiBriefcase />,
  ],
  [
    "Client portals",
    "Secure spaces for documents, collaboration, approvals, progress, and communication.",
    <FiShield />,
  ],
  [
    "CRM optimisation",
    "Better processes for relationships, pipeline management, lead assignment, and follow-up.",
    <FiBarChart2 />,
  ],
  [
    "Practice automation",
    "Give teams back time by simplifying repeatable administrative and operational work.",
    <FiLayers />,
  ],
];

const impactPoints = [
  "A clearer, more credible market presence",
  "Better client onboarding and collaboration",
  "Connected practice operations",
  "Commercial decisions backed by insight",
];

const faqs = [
  [
    "Can you help our firm generate more qualified enquiries?",
    "Yes. We create clear service pages, strong calls to action, useful content, and enquiry journeys that build trust with the right prospects.",
  ],
  [
    "Can clients securely share documents with our team?",
    "Yes. We can build secure client portals for document sharing, updates, approvals, and ongoing collaboration.",
  ],
  [
    "Can you automate follow-ups with new leads?",
    "Yes. CRM workflows can automatically assign leads, schedule follow-ups, send reminders, and improve response times.",
  ],
  [
    "Can you create dashboards for our firm?",
    "Yes. We can build dashboards that give teams visibility into leads, client work, performance, and operational progress.",
  ],
  [
    "What solutions do you build for professional service firms?",
    "We build professional websites, client portals, knowledge platforms, CRM journeys, dashboards, automation, and bespoke business applications.",
  ],
  [
    "Can you improve our client onboarding experience?",
    "Yes. We map each step, remove friction, and create secure digital workflows that feel clear and professional.",
  ],
  [
    "Can you work with our CRM and existing systems?",
    "Yes. We plan integrations that reduce duplicate work, connect relevant systems, and improve operational visibility.",
  ],
];

function ProfessionalHeroVisual() {
  return (
    <div className="professional-hero-visual" aria-hidden="true">
      <div className="professional-orbit orbit-two" />
      <div className="floating-business-card card-top">
        <FiBarChart2 />
        <div>
          <small>PERFORMANCE</small>
          <strong>+38.6%</strong>
        </div>
      </div>

      <div className="floating-business-card card-bottom">
        <FiShield />
        <div>
          <small>CLIENT TRUST</small>
          <strong>SECURED</strong>
        </div>
      </div>

      <div className="professional-dot dot-one" />
      <div className="professional-dot dot-two" />
      <div className="professional-dot dot-three" />
    </div>
  );
}

function ProfessionalServices() {
  const [open, setOpen] = useState(0);

  return (
    <main className="professional-page sector-page">
      {/* HERO */}
      <section className="professional-hero sector-hero">
        <div className="professional-hero-copy">
          <span className="professional-label">
            Techlynx for Professional Services
          </span>

          <div className="professional-kicker-line">
            <span>STRATEGY</span>
            <i />
            <span>TECHNOLOGY</span>
            <i />
            <span>GROWTH</span>
          </div>

          <h1>
            Expertise that is easier to <em>choose</em>
          </h1>

          <p>
            We turn complex expertise into clear digital experiences that
            build confidence, win work, strengthen client relationships, and
            streamline delivery.
          </p>

          <div className="professional-hero-actions">
            <a href="#professional-solutions" className="professional-button">
              Explore solutions <FiArrowUpRight />
            </a>

            <div className="professional-hero-status">
              <span className="status-pulse" />
              <span>Digital practice systems</span>
            </div>
          </div>
        </div>

        <ProfessionalHeroVisual />

        <div className="professional-scroll-indicator">
          <span>SCROLL TO EXPLORE</span>
          <div />
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        id="professional-solutions"
        className="professional-capabilities sector-capabilities"
      >
        <div className="professional-section-heading">
          <div>
            <span>Professional services technology</span>
            <h2>
              A sharper digital edge for <em>expert teams</em>
            </h2>
          </div>

          <p>
            From first introduction to long-term delivery, we create connected
            digital tools that make your expertise distinct, accessible, and
            easier to work with.
          </p>
        </div>

        <div className="professional-capability-grid">
          {capabilityCards.map((item, index) => (
            <article
              className="professional-capability-card"
              key={item.title}
              style={{ "--card-delay": `${index * 120}ms` }}
            >
              <div className="professional-card-image">
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="professional-card-image-shade" />
                <span>0{index + 1}</span>
              </div>

              <div className="professional-icon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <div className="professional-card-arrow">
                <FiArrowUpRight />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="professional-services sector-services">
        <div className="professional-section-heading services-heading">
          <div>
            <span>Where we add value</span>
            <h2>
              More clarity at every <em>client moment</em>
            </h2>
          </div>

          <div className="professional-heading-side">
            <div className="heading-number">04</div>
            <p>
              Connected digital foundations for modern firms, consultancies,
              agencies, advisory teams, and professional organisations.
            </p>
          </div>
        </div>

        <div className="professional-service-grid">
          {services.map(([title, text, icon], index) => (
            <article
              className="professional-service-card"
              key={title}
              style={{ "--service-delay": `${index * 100}ms` }}
            >
              <div className="service-number">0{index + 1}</div>

              <div className="service-icon">{icon}</div>

              <h3>{title}</h3>

              <p>{text}</p>

              <div className="service-line" />

              <FiArrowUpRight className="service-arrow" />
            </article>
          ))}
        </div>
      </section>

      {/* VISUAL STORY */}
      <section className="professional-story">
        <div className="professional-story-image">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=85"
            alt="Professional team collaborating"
            loading="lazy"
          />

          <div className="story-image-overlay" />

          <div className="story-floating-label">
            <span>CONNECTED TEAMS</span>
            <b>TECHLYNX / 2026</b>
          </div>

          <div className="story-corner top-left" />
          <div className="story-corner bottom-right" />
        </div>

        <div className="professional-story-copy">
          <span>Digital transformation</span>

          <h2>
            Make expertise <em>visible</em>
          </h2>

          <p>
            Your knowledge is valuable. Your digital experience should make
            that value immediately understandable to prospects, clients, and
            internal teams.
          </p>

          <div className="story-points">
            <div>
              <FiUsers />
              <span>Client-first experiences</span>
            </div>

            <div>
              <FiMessageSquare />
              <span>Clearer communication</span>
            </div>

            <div>
              <FiBarChart2 />
              <span>Actionable business insight</span>
            </div>
          </div>

          <Link to="/contact" className="professional-outline-button">
            Discuss your requirements <FiArrowUpRight />
          </Link>
        </div>
      </section>

      {/* RESULTS */}
      <section className="professional-results sector-results">
        <div className="professional-results-head">
          <span>Built for momentum</span>

          <h2>
            Technology that helps your firm <em>move forward</em>
          </h2>

          <p>
            The goal is not technology for its own sake. It is a better way
            for your team to attract, serve, collaborate, measure, and grow.
          </p>
        </div>

        <div className="professional-result-points">
          {impactPoints.map((point, index) => (
            <div
              className="professional-result-point"
              key={point}
              style={{ "--result-delay": `${index * 120}ms` }}
            >
              <div className="result-icon">
                <FiCheckCircle />
              </div>

              <span>{point}</span>

              <b>0{index + 1}</b>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="professional-faq sector-faq">
        <div className="professional-faq-intro">
          <span>Frequently asked questions</span>

          <h2>
            Questions deserve <em>clear answers</em>
          </h2>

          <p>
            Talk with Techlynx about the digital experience, client platform,
            automation, or business system you want to improve next.
          </p>

          <Link to="/contact" className="professional-button">
            Start a conversation <FiArrowUpRight />
          </Link>

          <div className="faq-decoration">
            <div />
            <div />
            <div />
          </div>
        </div>

        <div className="professional-faq-list">
          {faqs.map(([question, answer], index) => (
            <article
              className={
                open === index
                  ? "professional-faq-item is-open"
                  : "professional-faq-item"
              }
              key={question}
            >
              <button
                type="button"
                onClick={() =>
                  setOpen(open === index ? -1 : index)
                }
                aria-expanded={open === index}
              >
                <span>{question}</span>

                <div className="faq-icon">
                  {open === index ? <FiX /> : <FiPlus />}
                </div>
              </button>

              {open === index && (
                <div className="professional-faq-answer">
                  <p>{answer}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="professional-closing">
        <div className="closing-background-image">
          <img
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1800&q=85"
            alt=""
            loading="lazy"
          />
          <div />
        </div>

        <div className="professional-closing-content">
          <span>Build a stronger professional future</span>

          <h2>
            Turn expertise into <em>digital opportunity</em>
          </h2>

          <p>
            Whether you need a professional website, client portal, connected
            CRM, automation, dashboard, or a broader digital roadmap, Techlynx
            can help turn your ambition into a polished digital product.
          </p>

          <Link to="/contact" className="professional-button">
            Start a project <FiArrowUpRight />
          </Link>
        </div>
      </section>
    </main>
  );
}

export default ProfessionalServices;